import { useEffect } from 'react';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { AppProviders } from '@/app/providers';
import { useAuthStore } from '@/shared/auth/store';
import { routeTree } from '@/routeTree.gen';

const router = createRouter({
  routeTree,
  // Must track Vite's `base`, otherwise every link is generated for the wrong
  // prefix when the bundle is served from a sub-path of a shared origin.
  // `import.meta.env.BASE_URL` is what Vite compiles `base` into.
  basepath: import.meta.env.BASE_URL,
  defaultPreload: 'intent',
  context: {
    auth: { user: null, isAuthenticated: false },
  },
  scrollRestoration: true,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export function App() {
  return (
    <AppProviders>
      <InnerRouter />
    </AppProviders>
  );
}

function InnerRouter() {
  const user = useAuthStore((s) => s.user);
  const queryClient = useQueryClient();

  // A session does not only end on the way into a page: the cookie can expire
  // (or be dropped in the other portal on this origin) while a page is open,
  // and then the first request to come back 401 clears the store. Where an
  // anonymous visitor belongs is `_auth`'s decision, but `beforeLoad` runs on
  // navigation only — so the router has to be asked to decide again. Without
  // this the page stayed where it was with "Unauthorized" instead of content.
  useEffect(() => {
    if (user) return;
    // Nothing cached outlives the session either: the next person to log in in
    // this tab (it is a login without a page load) must not be shown the menu,
    // filters or rows of the previous one.
    queryClient.clear();
    void router.invalidate();
  }, [user, queryClient]);

  return (
    <RouterProvider
      router={router}
      context={{
        auth: {
          user,
          isAuthenticated: !!user,
        },
      }}
    />
  );
}
